import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth, ROLES, getDefaultRoute } from "../../context/AuthContext";

const PrivateRoute = ({ component, requiredRole, requiredPermission }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading, hasPermission, refreshUser } = useAuth();

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/verify-admin-token`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const isAdmin = response?.data?.user?.isAdmin;
        const role = response?.data?.user?.role;

        if (isAdmin) {
          setIsAuthenticated(true);
          setUserRole(role);
          if (!user) {
            refreshUser();
          }
        } else {
          localStorage.removeItem("token");
          toast.error("Admin token expired");
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error verifying token: ", error);
        setIsAuthenticated(false);
        localStorage.removeItem("token");
        toast.error("Admin token expired");
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [component]);

  if (loading || authLoading) {
    return (
      <div className="content-wrapper">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={"/login"} />;
  }

  // Check role-based access
  if (requiredRole) {
    const currentRole = user?.role || userRole;

    // Handle single role or array of roles
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    if (!allowedRoles.includes(currentRole)) {
      toast.error("You don't have permission to access this page");
      return <Navigate to={getDefaultRoute(user)} />;
    }
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    toast.error("You don't have permission to access this page");
    return <Navigate to={getDefaultRoute(user)} />;
  }

  return component;
};

export default PrivateRoute;
